<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth";
import api from "@/utils/axios";
import { Menu } from "@/components/Base/Headless";
import Lucide from "@/components/Base/Lucide";

const { t, locale } = useI18n();
const authStore = useAuthStore();

const switchLanguage = async (lang: string) => {
    locale.value = lang;
    if (authStore.user) {
        try {
            // Update local store first for responsiveness
            const settings = { ...authStore.user.settings, lang };
            authStore.user.settings = settings;

            // Persist
            await api.put("/auth/profile", { settings });
        } catch (e) {
            console.error("Error saving language preference", e);
        }
    }
};

const flags = {
    gl: {
        label: 'Galego',
        icon: '🏴' // Fallback
    },
    es: {
        label: 'Español',
        icon: '🇪🇸'
    },
    en: {
        label: 'English',
        icon: '🇬🇧'
    }
};

defineProps<{
  placement?: "top-start" | "top" | "top-end" | "right-start" | "right" | "right-end" | "bottom-end" | "bottom" | "bottom-start" | "left-start" | "left" | "left-end";
}>();
</script>

<template>
    <Menu>
        <Menu.Button class="flex items-center p-2 rounded-md hover:bg-slate-100 dark:hover:bg-darkmode-400 transition-colors">
            <div class="w-6 h-4 mr-2 flex items-center justify-center overflow-hidden rounded-sm shadow-sm border border-slate-200">
                <svg v-if="locale === 'gl'" viewBox="0 0 640 480" class="w-full h-full object-cover">
                    <path fill="#fff" d="M0 0h640v480H0z"/>
                    <path fill="#0099d8" d="M0 0h213.33L640 426.67V480H426.67L0 53.33z"/>
                </svg>
                <svg v-else-if="locale === 'es'" viewBox="0 0 640 480" class="w-full h-full object-cover">
                    <path fill="#aa151b" d="M0 0h640v480H0z"/>
                    <path fill="#f1bf00" d="M0 120h640v240H0z"/>
                </svg>
                <svg v-else viewBox="0 0 640 480" class="w-full h-full object-cover">
                    <path fill="#012169" d="M0 0h640v480H0z"/>
                    <path fill="#FFF" d="M75 0l244 181 244-181h77v62L389 240l251 178v62h-77L319 299 75 480H0v-62l251-178L0 62V0h75z"/>
                    <path fill="#C8102E" d="M424 240L640 480h-67L319 281v199h-60V281L32 480H0v-26l259-199L0 42V0h36l223 184V0h60v184L544 0h96v26L424 240z"/>
                    <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/>
                    <path fill="#C8102E" d="M266 0v480h110V0H266zM0 185v110h640V185H0z"/>
                </svg>
            </div>
            <div class="text-slate-600 dark:text-slate-300 text-xs font-medium mr-auto truncate group-[.side-menu--collapsed]:hidden">
                {{ locale === 'es' ? 'Español' : (locale === 'en' ? 'English' : 'Galego') }}
            </div>
            <Lucide icon="ChevronUp" class="w-3 h-3 text-slate-400 ml-2 group-[.side-menu--collapsed]:hidden" />
        </Menu.Button>
        <Menu.Items class="w-48 mb-2" :placement="placement || 'top-start'">
            <Menu.Item @click="switchLanguage('es')" :class="{'bg-slate-100/60': locale === 'es'}">
                <div class="w-6 h-4 mr-2 overflow-hidden rounded-sm shadow-sm border border-slate-200">
                    <svg viewBox="0 0 640 480" class="w-full h-full object-cover">
                        <path fill="#aa151b" d="M0 0h640v480H0z"/>
                        <path fill="#f1bf00" d="M0 120h640v240H0z"/>
                    </svg>
                </div>
                Español
            </Menu.Item>
            <Menu.Item @click="switchLanguage('gl')" :class="{'bg-slate-100/60': locale === 'gl'}">
                <div class="w-6 h-4 mr-2 overflow-hidden rounded-sm shadow-sm border border-slate-200">
                        <svg viewBox="0 0 640 480" class="w-full h-full object-cover">
                        <path fill="#fff" d="M0 0h640v480H0z"/>
                        <path fill="#0099d8" d="M0 0h213.33L640 426.67V480H426.67L0 53.33z"/>
                    </svg>
                </div>
                Galego
            </Menu.Item>
            <Menu.Item @click="switchLanguage('en')" :class="{'bg-slate-100/60': locale === 'en'}">
                <div class="w-6 h-4 mr-2 overflow-hidden rounded-sm shadow-sm border border-slate-200">
                        <svg viewBox="0 0 640 480" class="w-full h-full object-cover">
                        <path fill="#012169" d="M0 0h640v480H0z"/>
                        <path fill="#FFF" d="M75 0l244 181 244-181h77v62L389 240l251 178v62h-77L319 299 75 480H0v-62l251-178L0 62V0h75z"/>
                        <path fill="#C8102E" d="M424 240L640 480h-67L319 281v199h-60V281L32 480H0v-26l259-199L0 42V0h36l223 184V0h60v184L544 0h96v26L424 240z"/>
                        <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/>
                        <path fill="#C8102E" d="M266 0v480h110V0H266zM0 185v110h640V185H0z"/>
                    </svg>
                </div>
                English
            </Menu.Item>
        </Menu.Items>
    </Menu>
</template>
